<template>
  <div class="freight-tables-filters">
    <div class="freight-tables-filters__left">
      <a-input-search
        v-model="searchValue"
        placeholder="Busque pelo código ou descrição"
        class="freight-tables-filters__search"
        allow-clear
        @search="emitFilterChange"
        @change="onInputChange"
      >
        <a-icon slot="prefix" type="search" style="color: rgba(0, 0, 0, 0.25)" />
      </a-input-search>

      <slot name="tabs" />
    </div>

    <div class="freight-tables-filters__actions">
      <a-button
        class="freight-tables-filters__btn"
        @click="$emit('open-filters')"
      >
        <a-icon type="control" /> Filtros
      </a-button>

      <div class="freight-tables-filters__period">
        <a-button
          class="freight-tables-filters__btn"
          @click="periodOpen = true"
        >
          <a-icon type="calendar" /> {{ periodLabel }}

          <span
            v-if="hasPeriod"
            class="freight-tables-filters__period-clear"
            title="Limpar período"
            @click.stop="clearPeriod"
          >
            <a-icon type="close-circle" theme="filled" />
          </span>
        </a-button>

        <a-range-picker
          v-model="periodValue"
          class="freight-tables-filters__period-picker"
          :open="periodOpen"
          :locale="datePickerLocale"
          :format="DATE_DISPLAY_FORMAT"
          :placeholder="['Data inicial', 'Data final']"
          @openChange="periodOpen = $event"
          @change="onPeriodChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment";
import "moment/locale/pt-br";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";

const DATE_DISPLAY_FORMAT = "DD/MM/YYYY";
const DATE_PARAM_FORMAT = "YYYY-MM-DD";

export default {
  name: "FreightTablesFilters",
  props: {
    dtInicio: {
      type: String,
      default: null,
    },
    dtFim: {
      type: String,
      default: null,
    },
    search: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      searchValue: this.search,
      periodOpen: false,
      periodValue: [],
      datePickerLocale: ptBR,
      DATE_DISPLAY_FORMAT,
    };
  },
  computed: {
    hasPeriod() {
      return this.periodValue.length === 2;
    },
    periodLabel() {
      if (this.periodValue.length !== 2) return "Período Vigências";

      return `${this.periodValue[0].format(
        DATE_DISPLAY_FORMAT,
      )} - ${this.periodValue[1].format(DATE_DISPLAY_FORMAT)}`;
    },
  },
  watch: {
    dtInicio: "syncPeriod",
    dtFim: "syncPeriod",
    search(value) {
      if (value !== this.searchValue) {
        this.debouncedEmit.cancel();
        this.searchValue = value;
      }
    },
  },
  created() {
    this.debouncedEmit = debounce(this.emitFilterChange, 500);
    this.syncPeriod();
  },
  beforeDestroy() {
    this.debouncedEmit.cancel();
  },
  methods: {
    onInputChange() {
      this.debouncedEmit();
    },
    emitFilterChange() {
      this.$emit("filter-change", { search: this.searchValue });
    },
    syncPeriod() {
      this.periodValue =
        this.dtInicio && this.dtFim
          ? [
              moment(this.dtInicio, DATE_PARAM_FORMAT),
              moment(this.dtFim, DATE_PARAM_FORMAT),
            ]
          : [];
    },
    clearPeriod() {
      this.periodOpen = false;
      this.$emit("period-change", { dtInicio: null, dtFim: null });
    },
    onPeriodChange(dates) {
      this.periodOpen = false;

      if (!dates || dates.length !== 2) {
        this.$emit("period-change", { dtInicio: null, dtFim: null });
        return;
      }

      this.$emit("period-change", {
        dtInicio: dates[0].format(DATE_PARAM_FORMAT),
        dtFim: dates[1].format(DATE_PARAM_FORMAT),
      });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-filters {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  &__search {
    width: 305px;
    flex-shrink: 0;

    /deep/ .ant-input {
      border-radius: 6px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__period {
    position: relative;
  }

  &__period-clear {
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    color: @color-text-muted;

    &:hover {
      color: @color-text-medium;
    }

    .ant-btn > & {
      pointer-events: auto;
      cursor: pointer;
    }
  }

  &__period-picker {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 0;
    visibility: hidden;
  }

  &__btn {
    border-radius: 6px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: @color-text-medium;
  }
}
</style>

<style lang="less">
@import "~@/modules/shared/styles/form-controls.less";

.freight-tables-filters {
  .module-form-controls();
}
</style>
